import dynamic from 'next/dynamic';
import { Suspense } from 'react';

import { SOCIAL_CARD_TYPE, socialCardKeys } from '@/src/constants/socialcard.constant';
import { WIDGET_TYPE } from '@/src/constants/wall.constant';
import SocialVideoFallback from '@/src/components/common/SocialVideoFallback';

import AuroraGlassCard from './AuroraGlassCard';
import BroadsheetCard from './BroadsheetCard';
import ColorbandCard from './ColorbandCard';
import ToplineCard from './ToplineCard';
import InkBlockCard from './InkBlockCard';
import PinboardCard from './PinboardCard';
import PrismEdgeCard from './PrismEdgeCard';
import MegaphoneCard from './MegaphoneCard';
import ScoreBlockCard from './ScoreBlockCard';
import SpotlightCard from './SpotlightCard';

const SocialVideoCard = dynamic(() => import('@/src/components/common/SocialVideoCard'), { suspense: true });

/**
 * Dispatchers for the studio card set (widget types 10-19).
 *
 * Matches the shape of the existing Get*Card helpers: called as a plain
 * function from the wall / carousel / marquee renderers, returns an element,
 * holds no hooks of its own.
 *
 * Videos imported from a social platform (YouTube, Loom, ...) keep using the
 * shared SocialVideoCard, exactly like every other widget type does.
 * Everything else - text, imported reviews, uploaded video, audio and picture
 * - is handled inside each card via StudioBody.
 */
const makeGetCard = (CardComponent) => (props) => {
  const { socialCardType, cardItem, cardWidth, cardBackgroundObject, darkModeEnabled, cardFontColor, refresh, fontStyle } = props;

  /* eslint-disable-next-line eqeqeq */
  if (socialCardType == SOCIAL_CARD_TYPE.VIDEO && cardItem && cardItem[socialCardKeys.sourcePlatform]) {
    return (
      <Suspense
        fallback={
          <SocialVideoFallback cardItem={cardItem} cardWidth={cardWidth} darkModeEnabled={darkModeEnabled} />
        }
      >
        <SocialVideoCard
          cardItem={cardItem}
          cardWidth={cardWidth}
          cardBackgroundObject={cardBackgroundObject}
          darkModeEnabled={darkModeEnabled}
          cardFontColor={cardFontColor}
          refresh={refresh}
          fontStyle={fontStyle}
        />
      </Suspense>
    );
  }

  return <CardComponent {...props} />;
};

export const GetAuroraGlassCard = makeGetCard(AuroraGlassCard);
export const GetBroadsheetCard = makeGetCard(BroadsheetCard);
export const GetColorbandCard = makeGetCard(ColorbandCard);
export const GetToplineCard = makeGetCard(ToplineCard);
export const GetInkBlockCard = makeGetCard(InkBlockCard);
export const GetPinboardCard = makeGetCard(PinboardCard);
export const GetPrismEdgeCard = makeGetCard(PrismEdgeCard);
export const GetMegaphoneCard = makeGetCard(MegaphoneCard);
export const GetScoreBlockCard = makeGetCard(ScoreBlockCard);
export const GetSpotlightCard = makeGetCard(SpotlightCard);

/**
 * widget type -> renderer, for every surface that paints a wall (grid,
 * carousel, marquee). Keep new studio types here only - the call sites just
 * ask getStudioRenderer() and fall through when it returns null.
 */
const STUDIO_RENDERERS = {
  [WIDGET_TYPE.AURORA_GLASS_WIDGET_TYPE]: GetAuroraGlassCard,
  [WIDGET_TYPE.BROADSHEET_WIDGET_TYPE]: GetBroadsheetCard,
  [WIDGET_TYPE.COLORBAND_WIDGET_TYPE]: GetColorbandCard,
  [WIDGET_TYPE.TOPLINE_WIDGET_TYPE]: GetToplineCard,
  [WIDGET_TYPE.INK_BLOCK_WIDGET_TYPE]: GetInkBlockCard,
  [WIDGET_TYPE.PINBOARD_WIDGET_TYPE]: GetPinboardCard,
  [WIDGET_TYPE.PRISM_EDGE_WIDGET_TYPE]: GetPrismEdgeCard,
  [WIDGET_TYPE.MEGAPHONE_WIDGET_TYPE]: GetMegaphoneCard,
  [WIDGET_TYPE.SCORE_BLOCK_WIDGET_TYPE]: GetScoreBlockCard,
  [WIDGET_TYPE.SPOTLIGHT_WIDGET_TYPE]: GetSpotlightCard,
};

/** Returns the studio renderer for a widget type, or null if it isn't one. */
export const getStudioRenderer = (widgetType) => STUDIO_RENDERERS[widgetType] || null;

export default getStudioRenderer;
